// Shared utility functions for driver data management

export const formatTime = (minutes) => {
  if (!minutes && minutes !== 0) return '0:00';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}:${mins.toString().padStart(2, '0')}`;
};

export const getExpirationStatus = (expirationDate, type = 'general') => {
  if (!expirationDate) return { status: 'Unknown', color: 'bg-gray-100 text-gray-800' };

  let expDate = null;

  if (expirationDate instanceof Date) {
    expDate = expirationDate;
  } else if (typeof expirationDate === 'object' && expirationDate !== null) {
    if (typeof expirationDate.toDate === 'function') {
      const converted = expirationDate.toDate();
      expDate = Number.isNaN(converted?.getTime?.()) ? null : converted;
    } else if (typeof expirationDate.seconds === 'number') {
      const millis = (expirationDate.seconds * 1000) + Math.floor((expirationDate.nanoseconds || 0) / 1_000_000);
      const converted = new Date(millis);
      expDate = Number.isNaN(converted.getTime()) ? null : converted;
    }
  }

  if (!expDate) {
    const parsed = new Date(expirationDate);
    expDate = Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (!expDate) {
    return { status: 'Unknown', color: 'bg-gray-100 text-gray-800' };
  }
  const today = new Date();
  const daysUntilExpiry = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));
  
  if (daysUntilExpiry < 0) {
    return {
      status: 'Expired',
      color: 'bg-red-100 text-red-800 border-red-300',
      days: Math.abs(daysUntilExpiry),
      urgency: 'high'
    };
  }
  
  if (daysUntilExpiry <= 30) {
    return {
      status: type === 'medical' ? 'Expiring Soon' : 'Expires Soon',
      color: 'bg-red-100 text-red-800 border-red-300',
      days: daysUntilExpiry,
      urgency: 'high'
    };
  }
  
  if (daysUntilExpiry <= 60) {
    return {
      status: 'Expires Soon',
      color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      days: daysUntilExpiry,
      urgency: 'medium'
    };
  }
  
  return {
    status: 'Valid',
    color: 'bg-green-100 text-green-800 border-green-300',
    days: daysUntilExpiry,
    urgency: 'low'
  };
};

export const calculateHOSRemaining = (hosData) => {
  if (!hosData) return null;
  
  const limits = {
    maxDriving: 660, // 11 hours
    maxOnDuty: 840,  // 14 hours
    maxCycle: 4200   // 70 hours
  };
  
  return {
    remainingDriving: Math.max(0, limits.maxDriving - (hosData.drivingTimeToday || 0)),
    remainingOnDuty: Math.max(0, limits.maxOnDuty - (hosData.onDutyTimeToday || 0)),
    remainingCycle: Math.max(0, limits.maxCycle - (hosData.cycleTime || 0)),
    breakRequired: hosData.breakRequired || (hosData.continuousDriving >= 480), // 8 hours
    urgencyLevel: hosData.urgencyLevel || calculateUrgencyLevel(hosData)
  };
};

const calculateUrgencyLevel = (hosData) => {
  const limits = { maxDriving: 660, maxOnDuty: 840 };
  
  if (hosData.breakRequired || hosData.continuousDriving >= 480) return 'high';
  
  const drivingRatio = (hosData.drivingTimeToday || 0) / limits.maxDriving;
  const onDutyRatio = (hosData.onDutyTimeToday || 0) / limits.maxOnDuty;
  
  if (drivingRatio >= 0.9 || onDutyRatio >= 0.9) return 'high';
  if (drivingRatio >= 0.8 || onDutyRatio >= 0.8) return 'medium';
  return 'low';
};

export const getEnhancedHOSStatus = (driver) => {
  if (!driver.hosData) {
    return {
      display: driver.hosStatus || 'Unknown',
      classes: 'bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-medium',
      urgency: 'low'
    };
  }
  
  const { hosStatus, hosData } = driver;
  const hosRemaining = calculateHOSRemaining(hosData);
  
  if (hosRemaining.breakRequired) {
    return {
      display: 'Break Required',
      classes: 'bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium border border-red-300',
      urgency: 'high'
    };
  }
  
  if (hosStatus === 'Off Duty') {
    return {
      display: 'Off Duty',
      classes: 'bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-medium',
      urgency: 'low'
    };
  }
  
  const timeDisplay = hosRemaining.remainingDriving > 0 
    ? ` (${formatTime(hosRemaining.remainingDriving)} left)` 
    : '';
  
  let classes = '';
  switch (hosRemaining.urgencyLevel) {
    case 'high':
      classes = 'bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium border border-red-300';
      break;
    case 'medium':
      classes = 'bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium border border-yellow-300';
      break;
    default:
      classes = 'bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium';
  }
  
  return {
    display: `${hosStatus}${timeDisplay}`,
    classes,
    urgency: hosRemaining.urgencyLevel
  };
};

export const getHOSButtonStyle = (urgencyLevel, breakRequired = false) => {
  if (breakRequired) {
    return 'inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-colors duration-150 border border-red-300';
  }
  
  switch (urgencyLevel) {
    case 'high':
      return 'inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-colors duration-150 border border-red-300';
    case 'medium':
      return 'inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-yellow-100 text-yellow-700 hover:bg-yellow-200 transition-colors duration-150 border border-yellow-300';
    default:
      return 'inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-green-100 text-green-700 hover:bg-green-200 transition-colors duration-150';
  }
};

export const getHOSButtonText = (urgencyLevel, breakRequired = false) => {
  if (breakRequired) return 'Compliance ⚠️';
  if (urgencyLevel === 'high') return 'Compliance ⚠️';
  if (urgencyLevel === 'medium') return 'Compliance ⏰';
  return 'Compliance';
};

export const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString();
};

export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString();
};
